import { useEffect, useState } from 'react';
import {
  getBillingCyclePayerResponse,
  getNoaPayerResponse,
} from '../../../services/api';
import type { PayerResponseSummary } from '../../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';
import type { PayerReasonKind } from './PayerReasonDrawer';

export interface PayerResponseState {
  summary: PayerResponseSummary | null;
  isLoading: boolean;
  errorMessage: string | null;
}

/**
 * The shared payer-response fetch for the drawer and the badge popover
 * (EMR-1268). One hook so the two surfaces cannot disagree about the reasons
 * they show, or about how a failure is reported.
 *
 * Fetches only while `enabled` — both callers open on demand, so a list page of
 * 25 rows issues no request until the biller asks for one.
 */
export const usePayerResponse = (
  kind: PayerReasonKind,
  id: string,
  enabled: boolean,
  retryRequest = 0
): PayerResponseState => {
  const [summary, setSummary] = useState<PayerResponseSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const response =
          kind === 'claim'
            ? await getBillingCyclePayerResponse({
                path: { billing_cycle_id: id },
              })
            : await getNoaPayerResponse({
                path: { noa_id: id },
              });
        // The whole result, never `response.error` alone: the status lives on
        // `response.response`, and without it getBusinessErrorMessage cannot
        // tell a 5xx body from a 4xx business message.
        if (response.error || !response.data) throw response;
        if (!cancelled) setSummary(response.data);
      } catch (error) {
        // Payer text and patient data both travel in this response, so the raw
        // string never reaches the console or a toast.
        if (!cancelled) {
          setSummary(null);
          setErrorMessage(
            getBusinessErrorMessage(error) ??
              'Unable to load the payer response.'
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, kind, id, retryRequest]);

  return { summary, isLoading, errorMessage };
};
